import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import threadService from '../services/threadService';

/**
 * Message interface matching backend structure
 * Compatible with ThreadService_Message from threadService.d.ts
 */
interface Message {
  id: string;
  content: string;
  sender: 'usuario' | 'assistant';  // Backend values: 'usuario' for user, 'assistant' for N8N
  timestamp: Date | string;
  message_type?: string;  // Optional to match ThreadService_Message
  thread_id?: string;     // Optional to match ThreadService_Message
  color?: string;
  agentName?: string;
  _isOptimistic?: boolean; // Flag for optimistic updates
  attachments?: Array<{  // ✅ NEW: File attachments with thumbnails
    id: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    fileUrl: string;
    thumbnailUrl?: string | null;
    uploadedAt: Date | string;
    // #157/#274 — estado de vectorización RAG por-adjunto (se actualiza en vivo vía
    // WebSocket `file_rag_status` y sobrevive al reload vía getMessages).
    ragStatus?: string | null;
    ragError?: string | null;
    warningCount?: number | null;
    lastWarning?: string | null;
  }>;
  feedback?: {
    rating: 'positive' | 'negative';
    category?: string;
    comment?: string;
  } | null;
  /**
   * #898 — este mensaje abrió un turno cuyo plan quedó a medias por un ⏹.
   * Sale de `metadata.stopped_plan` (BD) o del parche en vivo del evento
   * `stream-cancelled-plan`; el render no distingue cuál de los dos.
   */
  stoppedPlan?: {
    executionId: string;
    stepsCompleted: number;
    stepCount: number | null;
  } | null;
  [key: string]: any;  // Allow additional properties from backend
}

/**
 * Agent status from backend
 * Compatible with ThreadService_AgentStatus from threadService.d.ts
 */
interface AgentStatus {
  agentName: string | null;
  currentTask: string | null;
  lastUpdated?: string | null;  // Match ThreadService_AgentStatus
  color?: string;
  /** Antigüedad de `lastUpdated` en ms según el reloj de la BD (#780). */
  ageMs?: number | null;
}

/**
 * Query data structure - messages + agent status
 */
interface QueryData {
  messages: Message[];
  agentStatus: AgentStatus | null;
}

/**
 * Context type for mutation optimistic updates
 */
interface MutationContext {
  previousMessages?: Message[];
  optimisticMessage: Message;
}

/**
 * Hook for message management with React Query
 *
 * ✅ CRITICAL FEATURES:
 * - Automatic reconciliation: React Query deduplicates by ID
 * - Optimistic updates: Instant UI feedback
 * - Background sync: Auto-refetch every 60s
 * - Retry logic: Auto-retry on failures
 * - No race conditions: Single source of truth (DB)
 *
 * This hook replaces the custom useChatMessages implementation
 * with battle-tested React Query patterns.
 */
/**
 * Ordena los mensajes de forma determinista para que no bailen en pantalla.
 *
 * ⚠️ Vive a nivel de MÓDULO, y eso no es estilo: React Query solo reutiliza el
 * resultado memoizado cuando `options.select === this.#selectFn`
 * (`queryObserver.js:287`). Una flecha inline se recrea en cada render, falla
 * esa identidad SIEMPRE y obliga a reordenar el array —y a devolver un objeto
 * nuevo— en cada render, incluso cuando no ha cambiado ni un mensaje. Con
 * streaming a 30 deltas/s eso son 30 reordenaciones por segundo y, peor, un
 * `messages` con identidad nueva que rompe el bail-out de las burbujas (#930).
 *
 * Criterio de orden: timestamp asc; a igualdad, el mensaje del usuario primero
 * (si N8N responde en el mismo milisegundo); y a igualdad de emisor, por id
 * (los optimistas empiezan por "temp-", los reales son UUID).
 */
const selectSortedMessages = (data: QueryData): QueryData => {
  // El timestamp se convierte UNA vez por mensaje, no dos por comparación:
  // un sort hace ~n·log(n) comparaciones, así que `new Date()` dentro del
  // comparador asignaba ~2·n·log(n) objetos por pasada.
  const withTime = data.messages.map((message) => ({
    message,
    time: new Date(message.timestamp).getTime(),
  }));

  withTime.sort((a, b) => {
    if (a.time !== b.time) return a.time - b.time;
    if (a.message.sender !== b.message.sender) {
      return a.message.sender === 'usuario' ? -1 : 1;
    }
    return a.message.id.localeCompare(b.message.id);
  });

  return {
    messages: withTime.map((entry) => entry.message),
    agentStatus: data.agentStatus,
  };
};

export function useMessages(threadId: string | null) {
  const queryClient = useQueryClient();

  /**
   * Query messages from backend
   * React Query automatically:
   * - Caches results by queryKey
   * - Deduplicates concurrent requests
   * - Refetches in background
   * - Retries on failures
   *
   * ✅ FIX: Now returns { messages, agentStatus } as QueryData
   * This makes agentStatus reactive (causes re-render when it changes)
   * Previously used a ref which didn't trigger re-renders
   */
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery<QueryData, Error, QueryData>({
    queryKey: ['messages', threadId],
    queryFn: async (): Promise<QueryData> => {
      // ✅ CRITICAL: Always return empty data if no threadId
      // Do NOT throw error, just return empty to avoid triggering error state
      if (!threadId) {
        console.log('[useMessages] No threadId, returning empty data');
        return { messages: [], agentStatus: null };
      }

      console.log(`[useMessages] 🔍 Fetching messages for thread ${threadId}`);
      const response = await threadService.getMessages(threadId);
      const fetchedMessages = response?.messages || [];
      const agentStatus = response?.agentStatus || null;

      console.log(`[useMessages] 📦 Fetched ${fetchedMessages.length} messages, agentStatus:`, agentStatus);

      // Normalize: promote metadata.actions to top-level for ChatMessages.
      // #494: los mensajes con suppressed_delivery (respuesta espuria de un
      // turno cancelado, DEC-7) NO se renderizan — quedan solo en BD/ledger.
      // cancelledByUser (sin suppressed) = parcial visible congelado (DEC-1),
      // promocionado para el separador ⏹ Detenido. Solo corre en el fetch REST;
      // los mensajes inyectados por WS traen el flag directo en el objeto.
      const normalizedMessages = fetchedMessages
        .filter((msg: any) => msg.metadata?.suppressed_delivery !== true)
        .map((msg: any) => ({
          ...msg,
          actions: msg.actions || msg.metadata?.actions || null,
          cancelledByUser: msg.cancelledByUser === true || msg.metadata?.cancelled_by_user === true,
          // #898: el contador del plan cortado ya no es efímero. El backend lo
          // sella en el mensaje del usuario que abrió el turno cuando hubo pasos
          // ejecutados, así que sobrevive a recargar y —lo que importaba— al
          // RETRY, que apila la respuesta nueva sin mencionar el intento previo.
          stoppedPlan: msg.metadata?.stopped_plan
            ? {
                executionId: msg.metadata.stopped_plan.execution_id,
                stepsCompleted: msg.metadata.stopped_plan.steps_completed ?? 0,
                stepCount: msg.metadata.stopped_plan.step_count ?? null,
              }
            : null,
        }));

      return { messages: normalizedMessages, agentStatus };
    },
    // ✅ CRITICAL: Don't use enabled - it causes initial fetch when enabled changes from false→true
    // Instead, queryFn returns empty when no threadId (no-op)
    // enabled: !!threadId, // ❌ REMOVED: This causes automatic fetch on threadId change
    staleTime: Infinity,  // ✅ Keep data fresh forever - rely on mutations & WebSocket

    // ✅ CRITICAL: Disable ALL automatic refetching to prevent race conditions
    //
    // Race condition scenario WITHOUT these settings:
    //   1. User sends "hola" → new thread created
    //   2. threadId changes: null → uuid
    //   3. React Query detects new queryKey → auto-fetches
    //   4. Fetch arrives at backend BEFORE message is saved
    //   5. Backend returns [] (empty)
    //   6. React Query updates cache with []
    //   7. Optimistic message + WebSocket message disappear
    //
    // Solution: Disable ALL automatic fetching
    //   - New threads: NO fetch (messages come from optimistic + WebSocket)
    //   - Existing threads: Manual fetch via refetch() in useEffect
    //   - Updates: Via optimistic updates + WebSocket only
    refetchOnMount: false,       // Don't fetch when component mounts/remounts
    refetchOnWindowFocus: false, // Don't fetch when user returns to tab
    refetchOnReconnect: false,   // Don't fetch when internet reconnects
    refetchInterval: false,      // Don't poll - WebSocket handles updates
    initialData: { messages: [], agentStatus: null }, // Start with empty data (prevents loading state flicker)

    select: selectSortedMessages
  });

  // Extract messages and agentStatus from query data
  const messages = data?.messages || [];
  const initialAgentStatus = data?.agentStatus || null;

  /**
   * File metadata for optimistic updates
   */
  interface FileMetadata {
    fileName: string;
    fileType: string;
    fileSize: number;
    thumbnailDataUrl: string | null;
  }

  /**
   * Send message mutation with optimistic update
   *
   * ✅ CRITICAL: This handles the race condition that caused message disappearance
   *
   * Flow:
   * 1. onMutate: Add optimistic message immediately (instant UI feedback)
   * 2. mutationFn: Save to backend (async)
   * 3. onSuccess: Replace optimistic with real message (has DB ID)
   * 4. onError: Rollback to previous state
   *
   * ✅ NEW: Accepts optional overrideThreadId for new thread creation
   * ✅ NEW: Accepts filesMetadata for optimistic update with thumbnails
   */
  const sendMessage = useMutation<Message, Error, { content: string; files?: File[]; filesMetadata?: FileMetadata[]; overrideThreadId?: string; onProgress?: (progress: number) => void; signal?: AbortSignal }, MutationContext>({
    mutationFn: async ({
      content,
      files = [],
      filesMetadata, // ✅ NEW: Metadata for optimistic update (not sent to backend)
      overrideThreadId,
      onProgress,
      signal,
    }: {
      content: string;
      files?: File[];
      filesMetadata?: FileMetadata[];
      overrideThreadId?: string;
      onProgress?: (progress: number) => void;
      signal?: AbortSignal;
    }) => {
      const targetThreadId = overrideThreadId || threadId;
      if (!targetThreadId) throw new Error('No thread ID');

      console.log(`[useMessages] Sending message to thread ${targetThreadId}`, overrideThreadId ? '(override)' : '(from hook)');
      const savedMessage = files.length > 0
        ? await threadService.uploadMessageWithProgress(targetThreadId, content, files, filesMetadata, { onProgress, signal }) as Message
        : await threadService.createMessage(targetThreadId, content, files, filesMetadata) as Message;
      console.log(`[useMessages] Message saved with ID: ${savedMessage.id}`);
      console.log(`[useMessages] 🔍 Backend response structure:`, JSON.stringify(savedMessage, null, 2));

      return savedMessage;
    },

    // ✅ CRITICAL: Optimistic update (instant UI feedback)
    onMutate: async ({ content, files, filesMetadata, overrideThreadId }) => {
      const targetThreadId = overrideThreadId || threadId;
      if (!targetThreadId) {
        console.error('[useMessages] ❌ No threadId for optimistic update');
        throw new Error('No thread ID for optimistic update');
      }

      // Cancel any ongoing queries to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ['messages', targetThreadId] });

      // Snapshot previous state for rollback (extract messages from QueryData)
      const previousData = queryClient.getQueryData<QueryData>(['messages', targetThreadId]);
      const previousMessages = previousData?.messages;

      // Create optimistic message (temporary ID)
      const optimisticMessage: Message = {
        id: `temp-${Date.now()}-${Math.random()}`,
        content,
        sender: 'usuario',
        timestamp: new Date(),
        message_type: files && files.length > 0 ? 'file' : 'text',
        thread_id: targetThreadId,
        _isOptimistic: true, // Mark as optimistic for debugging

        // ✅ NEW: Include attachments with client-side thumbnails
        ...(filesMetadata && filesMetadata.length > 0 && {
          attachments: filesMetadata.map((meta, index) => ({
            id: `temp-file-${Date.now()}-${index}`,
            fileName: meta.fileName,
            fileType: meta.fileType,
            fileSize: meta.fileSize,
            fileUrl: '', // Empty until backend upload completes
            thumbnailUrl: meta.thumbnailDataUrl, // ✅ CLIENT THUMBNAIL
            uploadedAt: new Date()
          }))
        })
      };

      // ✅ CRITICAL: Add optimistic message to cache immediately (preserve agentStatus)
      queryClient.setQueryData<QueryData>(
        ['messages', targetThreadId],
        (old) => ({
          messages: [...(old?.messages || []), optimisticMessage],
          agentStatus: old?.agentStatus || null
        })
      );

      console.log('[useMessages] ✅ Optimistic message added to cache:', {
        messageId: optimisticMessage.id,
        threadId: targetThreadId,
        isOverride: !!overrideThreadId,
        hasAttachments: !!optimisticMessage.attachments,
        attachmentCount: optimisticMessage.attachments?.length || 0
      });

      return { previousMessages, optimisticMessage };
    },

    // ✅ CRITICAL: Replace optimistic with real message on success
    onSuccess: (savedMessage, variables, context) => {
      const targetThreadId = variables.overrideThreadId || threadId;

      queryClient.setQueryData<QueryData>(
        ['messages', targetThreadId],
        (old) => {
          const oldMessages = old?.messages || [];
          // Remove optimistic message
          const filtered = oldMessages.filter(msg => msg.id !== context?.optimisticMessage.id);

          // ✅ Check if real message already exists (edge case: manual refetch happened)
          const exists = filtered.some(msg => msg.id === savedMessage.id);
          if (exists) {
            console.log('[useMessages] ⚠️ Real message already exists, skipping duplicate:', savedMessage.id);
            return { messages: filtered, agentStatus: old?.agentStatus || null };
          }

          // ✅ NEW: Merge backend and client thumbnails
          const mergedMessage = {
            ...savedMessage,
            attachments: savedMessage.attachments?.map((backendAttachment: any, index: number) => {
              const clientAttachment = context?.optimisticMessage.attachments?.[index];

              return {
                ...backendAttachment,
                // Prefer backend thumbnail, fallback to client thumbnail
                thumbnailUrl: backendAttachment.thumbnailUrl || clientAttachment?.thumbnailUrl || null
              };
            })
          };

          // Add real message from backend (with DB ID) with merged thumbnails
          const result = [...filtered, mergedMessage];

          console.log('[useMessages] ✅ Optimistic message replaced with real:', {
            optimisticId: context?.optimisticMessage.id,
            realId: savedMessage.id,
            threadId: targetThreadId,
            backendThumbnails: savedMessage.attachments?.map((a: any) => a.thumbnailUrl),
            clientThumbnails: context?.optimisticMessage.attachments?.map((a: any) => a.thumbnailUrl),
            finalThumbnails: mergedMessage.attachments?.map((a: any) => a.thumbnailUrl)
          });

          return { messages: result, agentStatus: old?.agentStatus || null };
        }
      );
    },

    // ✅ CRITICAL: Rollback on error
    onError: (error, variables, context) => {
      console.error('[useMessages] ❌ Send failed, rolling back:', error);

      const targetThreadId = variables.overrideThreadId || threadId;

      if (context?.previousMessages) {
        // Rollback messages but preserve current agentStatus
        const currentData = queryClient.getQueryData<QueryData>(['messages', targetThreadId]);
        queryClient.setQueryData<QueryData>(['messages', targetThreadId], {
          messages: context.previousMessages,
          agentStatus: currentData?.agentStatus || null
        });
        console.log('[useMessages] ✅ Rolled back to previous state for thread:', targetThreadId);
      }
    }
  });

  /**
   * Add message from WebSocket (agent responses)
   *
   * ✅ CRITICAL: This prevents duplicates using messageId from backend
   *
   * When N8N sends a response:
   * 1. Backend saves message and gets DB ID
   * 2. Backend sends WebSocket with messageId = DB ID
   * 3. Frontend receives and deduplicates by ID
   * 4. No race condition possible (ID is unique)
   *
   * ✅ PERFORMANCE: Memoized with useCallback to prevent re-renders
   * Without memoization, Chat.jsx useEffect would re-subscribe on every render
   */
  const addMessageFromWebSocket = useCallback((message: Message) => {
    // ✅ CRITICAL: Validate messageId exists (should come from backend now)
    if (!message.id) {
      console.error('[useMessages] ❌ WebSocket message missing ID! This will cause duplicates:', message);
      return;
    }

    queryClient.setQueryData<QueryData>(
      ['messages', threadId],
      (old) => {
        const oldMessages = old?.messages || [];
        // ✅ CRITICAL: Deduplicate by ID (prevents race condition)
        const exists = oldMessages.some(m => m.id === message.id);
        if (exists) {
          console.log('[useMessages] ⚠️ Duplicate WebSocket message ignored:', message.id);
          return old || { messages: [], agentStatus: null };
        }

        console.log('[useMessages] ✅ WebSocket message added:', message.id);
        return {
          messages: [...oldMessages, message],
          agentStatus: old?.agentStatus || null
        };
      }
    );
  }, [queryClient, threadId]); // ✅ Only recreate when queryClient or threadId changes

  const updateMessageFeedback = useCallback((messageId: string, feedback: Message['feedback']) => {
    queryClient.setQueryData<QueryData>(
      ['messages', threadId],
      (old) => {
        if (!old) return { messages: [], agentStatus: null };
        return {
          ...old,
          messages: old.messages.map(msg =>
            msg.id === messageId ? { ...msg, feedback } : msg
          )
        };
      }
    );
  }, [queryClient, threadId]);

  /**
   * Parchea el estado RAG de un adjunto (#274). El evento WebSocket `file_rag_status`
   * de chat trae `fileId` pero NO `messageId` → se busca el adjunto por fileId en todos
   * los mensajes del thread actual. Si el fileId no está (evento de otro thread/rol), no-op.
   * Espejo de updateMessageFeedback (patch inmutable de la caché React Query).
   */
  const updateAttachmentRagStatus = useCallback((
    fileId: string,
    patch: {
      ragStatus?: string | null;
      ragError?: string | null;
      warningCount?: number | null;
      lastWarning?: string | null; // #1270 — motivo del aviso, en vivo (antes solo al recargar)
    }
  ) => {
    if (!fileId) return;
    queryClient.setQueryData<QueryData>(
      ['messages', threadId],
      (old) => {
        if (!old) return { messages: [], agentStatus: null };
        let changed = false;
        const messages = old.messages.map(msg => {
          if (!msg.attachments || msg.attachments.length === 0) return msg;
          if (!msg.attachments.some(att => att.id === fileId)) return msg;
          changed = true;
          return {
            ...msg,
            attachments: msg.attachments.map(att =>
              att.id === fileId ? { ...att, ...patch } : att
            )
          };
        });
        // Sin cambios → devolver el objeto original (evita re-render innecesario).
        return changed ? { ...old, messages } : old;
      }
    );
  }, [queryClient, threadId]);

  /**
   * Pinta el contador del plan cortado sobre el mensaje que abrió el turno
   * (#898), a partir del evento `stream-cancelled-plan`.
   *
   * El backend lo sella en BD, pero solo cuando hubo pasos ejecutados y en una
   * escritura fire-and-forget que la UI no espera. Este parche local cubre el
   * hueco: la marca aparece en el acto (sin refetch — este hook tiene
   * `staleTime: Infinity` y el refetch automático desactivado a propósito) y con
   * el mismo shape que traerá el fetch REST después, así que el render no
   * distingue si el dato vino del socket o de la BD.
   *
   * Corolario deliberado: un corte con 0 pasos SÍ se ve aquí y NO se persiste —
   * se pierde al recargar, que es lo que le corresponde a un turno que no llegó
   * a hacer nada.
   *
   * Espejo de updateAttachmentRagStatus (patch inmutable de la caché).
   */
  const applyStoppedPlan = useCallback((
    messageId: string,
    plan: { executionId: string; stepsCompleted: number; stepCount: number | null }
  ) => {
    if (!messageId || !plan?.executionId) return;
    queryClient.setQueryData<QueryData>(
      ['messages', threadId],
      (old) => {
        if (!old) return old;
        let changed = false;
        const messages = old.messages.map(msg => {
          if (msg.id !== messageId) return msg;
          changed = true;
          return { ...msg, stoppedPlan: plan };
        });
        return changed ? { ...old, messages } : old;
      }
    );
  }, [queryClient, threadId]);

  return {
    messages,
    isLoading,
    error,
    applyStoppedPlan,
    sendMessage: sendMessage.mutate,
    isSending: sendMessage.isPending, // ✅ Use this instead of processingStatus
    addMessageFromWebSocket,
    refetch,
    updateMessageFeedback,
    updateAttachmentRagStatus,
    // ✅ FIX: Now reactive - will trigger re-render when agentStatus changes
    // Previously was a ref that didn't cause re-renders
    initialAgentStatus
  };
}
